import { useTranslation } from 'react-i18next';
import { Letterhead, PaperFoot } from '@/components/common';
import type { Clinic, Offer } from '@/types/api';
import { formatDate, formatRsd } from '../../chart/components';

type Props = {
  offer: Offer;
  clinic: Clinic | null;
  logoUrl: string | null;
};

/** The offer on the clinic's letterhead, laid out for one A4 page (also shown on screen). */
export function OfferPaper({ offer, clinic, logoUrl }: Props) {
  const { t } = useTranslation();
  const doctor = offer.doctor?.name ?? offer.createdBy.name;

  return (
    <article className="offer-paper">
      <Letterhead clinic={clinic} logoUrl={logoUrl} />

      <h1 className="offer-paper__title">{t('offers.paper.title')}</h1>
      <dl className="offer-paper__meta">
        <dt>{t('offers.paper.number')}</dt>
        <dd>{offer.number}</dd>
        <dt>{t('offers.paper.date')}</dt>
        <dd>{formatDate(offer.issuedAt)}</dd>
        <dt>{t('offers.paper.validUntil')}</dt>
        <dd>{formatDate(`${offer.validUntil}T12:00:00`)}</dd>
        <dt>{t('offers.paper.patient')}</dt>
        <dd>
          <strong>{offer.patientName}</strong>
        </dd>
      </dl>

      <h2 className="offer-paper__section">{t('offers.paper.plan')}</h2>
      <ol className="offer-paper__steps">
        {offer.steps.map((step, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: steps are plain text in a fixed order
          <li key={i}>{step}</li>
        ))}
      </ol>

      <div className="offer-paper__total">
        <span>{t('offers.paper.total')}</span>
        <strong>{formatRsd(offer.totalRsd)}</strong>
      </div>
      <p className="offer-paper__fine">{t('offers.paper.estimate')}</p>

      {offer.note && (
        <>
          <h2 className="offer-paper__section">{t('offers.paper.note')}</h2>
          <p className="offer-paper__note">{offer.note}</p>
        </>
      )}

      <div className="offer-paper__sign">
        <div>
          <span className="offer-paper__line" />
          <span>{doctor}</span>
          <span className="offer-paper__fine">{t('offers.paper.signature')}</span>
        </div>
      </div>

      <PaperFoot />
    </article>
  );
}
